import React, { useState, useEffect, useRef } from 'react';
import styles from '../styles/HomePage.module.css';
import imageStyles from '../styles/ImageStyles.module.css';
import ProjectSection from './ProjectSection';
import axios from 'axios';
import kaiyaIcon from '../assets/kaiya_icon.png'; // Replace with your futuristic AI assistant icon
import JupiterSwap from '../components/JupiterSwap';
import TokenSection from './TokenSection';


const BASE_URL = window.location.origin;
const API_URL = 'https://api.baksotiger.com';
console.log("Using API URL:", API_URL);

interface Message {
  agent: string;
  message: string;
}

const HomePage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const vantaRef = useRef<HTMLDivElement>(null);



  // Scroll chat to the bottom when new messages appear
  useEffect(() => {
    if (isChatOpen && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isChatOpen]);

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen && messages.length === 0) {
      setMessages([{ agent: 'BaksoAI', message: 'Hello! I’m BaksoAI, your AI assistant. How can I assist you in learning about Bakso or the $BAKSO ecosystem today?' }]);
    }
  };

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const convertLinksToHTML = (text: string): string => {
    if (!text) return '';

    // First, handle Markdown-style links: [text](url)
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let processedText = text.replace(markdownLinkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Then, make plain URLs clickable too (http/https URLs not already in a tag)
    // Use a more precise regex that won't capture HTML tags
    const urlRegex = /(?<!["\'\(\[<])https?:\/\/[^\s<>]+/g;
    processedText = processedText.replace(urlRegex, (url: string) => {
      // Only replace if it's not already inside an HTML tag
      if (/<a[^>]*>.*?<\/a>/.test(url)) {
        return url;
      }

      // Make sure to trim any trailing periods or commas that might be part of the sentence
      const cleanUrl = url.replace(/[.,;:]$/, '');
      const punctuation = url.slice(cleanUrl.length); // Capture any trimmed punctuation

      return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${punctuation}`;
    });

    // Replace newlines with <br> tags
    return processedText.replace(/\n/g, '<br />');
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const messageToSend = inputMessage;
    setInputMessage('');
    setMessages(prev => [...prev, { agent: 'You', message: messageToSend }]);
    setIsThinking(true);

    try {
      const fullApiUrl = `${API_URL}/api/kaiya_message`;

      const response = await fetch(fullApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: messageToSend })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Don't do HTML processing here - we'll handle it in the render
      setMessages(prev => [...prev, {
        agent: 'BaksoAI',
        message: data.message || 'No response received. How can I assist you?'
      }]);
    } catch (error) {
      console.error('Error sending message to BaksoAI:', error);
      setMessages(prev => [...prev, {
        agent: 'BaksoAI',
        message: 'Sorry, I encountered an error. How else can I assist you?'
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputMessage.trim()) {
      e.preventDefault();
      handleSendMessage();
    }
  };



  return (
    <div className={styles.homePage}>
      <div className={styles.heroSection}>
        <h1>$BAKSO</h1>
        <p>Token Information</p>
      </div>

      



      {/* Insert the Project Section component */}
      <TokenSection />

      

      {/* Chat Button */}
      <button className={styles.helpButton} onClick={toggleChat}>Ask BaksoAI</button>

      {/* Chat Window */}
      {isChatOpen && (
        <div className={styles.chatWindow}>
          <div className={styles.chatHeader}>
            <img src={kaiyaIcon} alt="BaksoAI" className={styles.kaiyaIcon} />
            <h3>BaksoAI - Your AI Helper</h3>
            <button className={styles.closeChat} onClick={toggleChat}>×</button>
          </div>
          <div className={styles.chatMessages} ref={chatContainerRef}>
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`${styles.chatMessage} ${msg.agent === 'You' ? styles.userMessage : styles.kaiyaMessage}`}
              >
                <strong>{msg.agent}:</strong> <span dangerouslySetInnerHTML={{ __html: convertLinksToHTML(msg.message) }} />
              </div>
            ))}
            {isThinking && (
              <div className={styles.chatMessage}>
                <strong>BaksoAI:</strong> Thinking<span className={styles.typingIndicator}></span>
              </div>
            )}
          </div>
          <div className={styles.chatInput}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask BaksoAI anything..."
              className={styles.messageInput}
            />
            <button onClick={handleSendMessage} className={styles.sendButton}>Send</button>
          </div>
        </div>
      )}

    </div>


  );
};

export default HomePage;